import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import ComplaintPic from "./complaint.webp";
import List from "../list";
import { CloudUpload } from "lucide-react";

// APIs
const SURVEYOR_API = "https://portal.lsb.go.ke/account/surveyors/";
const COMPLAINTS_API = "https://portal.lsb.go.ke/lsb/complaints/";

const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

function Complaints() {
    // --- Surveyor state ---
    const [surveyors, setSurveyors] = useState([]);
    const [loadingSurveyors, setLoadingSurveyors] = useState(true);
    const [surveyorError, setSurveyorError] = useState(null);
    const [searchVal, setSearchVal] = useState("");
    const [filteredSurveyors, setFilteredSurveyors] = useState([]);
    const [selectedSurveyor, setSelectedSurveyor] = useState(null);
    const [surveyorDropdownVisible, setSurveyorDropdownVisible] = useState(false);
    const surveyorSearchRef = useRef(null);

    // Form fields state
    const [subject, setSubject] = useState("");
    const [complaintDetails, setComplaintDetails] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [county, setCounty] = useState("");
    const [subcounty, setSubCounty] = useState("");
    const [files, setFiles] = useState([]);
    const [evidenceError, setEvidenceError] = useState("");
    const [confirmationChecked, setConfirmationChecked] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let isMounted = true;
        setLoadingSurveyors(true);
        setSurveyorError(null);

        function normalizeList(data) {
            let list =
                (Array.isArray(data) && data) ||
                (Array.isArray(data?.results) && data.results) ||
                [];
            return list.map(s => ({
                ...s,
                id: s.license_no,
                licence_number: s.license_no,
                name: s.name,
            }));
        }

        async function fetchAllSurveyors() {
            let allSurveyors = [];
            let url = SURVEYOR_API;
            let pageLimit = 200;
            let pageCount = 0;
            try {
                while (url && pageCount < pageLimit) {
                    const res = await fetch(url);
                    if (!res.ok) throw new Error("Failed to load");
                    const data = await res.json();
                    allSurveyors = allSurveyors.concat(normalizeList(data));
                    url = data.next;
                    pageCount++;
                }
                if (isMounted) {
                    setSurveyors(allSurveyors);
                    setFilteredSurveyors(allSurveyors.slice(0, 20));
                    setLoadingSurveyors(false);
                }
            } catch (e) {
                if (isMounted) {
                    setSurveyorError("Unable to fetch surveyors.");
                    setLoadingSurveyors(false);
                }
            }
        }

        fetchAllSurveyors();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        function handleClick(e) {
            if (
                surveyorSearchRef.current &&
                !surveyorSearchRef.current.contains(e.target)
            ) {
                setSurveyorDropdownVisible(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    useEffect(() => {
        if (!searchVal.trim()) {
            setFilteredSurveyors(surveyors.slice(0, 20));
            return;
        }
        const val = searchVal.trim().toLowerCase();
        setFilteredSurveyors(
            surveyors.filter(
                s =>
                    (`${s.name || ""}`.toLowerCase().includes(val)) ||
                    (`${s.licence_number || ""}`.toLowerCase().includes(val))
            ).slice(0, 20)
        );
    }, [searchVal, surveyors]);

    const handleSurveyorSelect = (surveyor) => {
        setSelectedSurveyor(surveyor);
        setSurveyorDropdownVisible(false);
        setSearchVal(surveyor.name || "");
    };

    const handleSurveyorInput = (e) => {
        setSearchVal(e.target.value);
        setSurveyorDropdownVisible(true);
        setSelectedSurveyor(null);
    };

    const handleFilesChange = (e) => {
        setEvidenceError("");
        let fileList = Array.from(e.target.files);
        let deduped = fileList.slice(0, 1);

        let invalids = deduped.filter(file => {
            const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
            const validExt = ALLOWED_EXTENSIONS.includes(ext);
            const validSize = file.size <= MAX_FILE_SIZE;
            return !validExt || !validSize;
        });

        if (invalids.length > 0) {
            const errorMessages = invalids.map(file => {
                const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
                if (!ALLOWED_EXTENSIONS.includes(ext)) {
                    return `File "${file.name}" has invalid type. Only ${ALLOWED_EXTENSIONS.join(", ")} allowed.`;
                }
                if (file.size > MAX_FILE_SIZE) {
                    return `File "${file.name}" exceeds 10MB size limit.`;
                }
                return null;
            }).filter(Boolean);
            setEvidenceError(errorMessages.join(" "));
            deduped = [];
        }

        setFiles(deduped);
        e.target.value = "";
    };

    const handleRemoveFile = () => {
        setFiles([]);
    };

    const isFormValid = () => {
        return (
            selectedSurveyor &&
            complaintDetails.trim() &&
            firstName.trim() &&
            lastName.trim() &&
            phone.trim() &&
            email.trim() &&
            county.trim() &&
            subcounty.trim() &&
            confirmationChecked &&
            files.length > 0 &&
            !evidenceError
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) {
            Swal.fire('Error!', 'Please fill out all required fields and resolve any errors.', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("license_no", selectedSurveyor.license_no);
            formData.append("subject", subject || "Complaint against Surveyor");
            formData.append("message", complaintDetails);
            formData.append("name", `${firstName.trim()} ${lastName.trim()}`);
            formData.append("email", email);
            formData.append("phone_number", phone);
            formData.append("county", county);
            formData.append("sub_county", subcounty);
            if (files[0]) {
                formData.append("document", files[0], files[0].name);
            }

            const response = await fetch(COMPLAINTS_API, {
                method: "POST",
                body: formData
            });

            if (!response.ok) {
                let errMsg = "Failed to submit complaint.";
                try {
                    const respData = await response.json();
                    if (respData?.detail) errMsg = respData.detail;
                    else if (typeof respData === "object" && respData) {
                        errMsg = Object.values(respData).map(v => Array.isArray(v) ? v.join(" ") : v).join(" ");
                    }
                } catch { }
                throw new Error(errMsg);
            }
            Swal.fire('Success!', 'Your complaint has been submitted successfully.', 'success');
            // Reset form
            setSubject("");
            setComplaintDetails("");
            setFirstName("");
            setLastName("");
            setPhone("");
            setEmail("");
            setCounty("");
            setSubCounty("");
            setFiles([]);
            setConfirmationChecked(false);
            setSelectedSurveyor(null);
            setSearchVal("");
        } catch (err) {
            Swal.fire('Error!', err.message || "Failed to submit complaint.", 'error');
        }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen">
            <div className="relative mb-12">
                <img
                    className="h-96 w-full object-cover object-center rounded-b-2xl shadow-2xl"
                    src={ComplaintPic}
                    alt="A gavel and law book"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-2xl"></div>
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center w-full px-4">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 text-center tracking-tight shadow-sm">
                        Submit a Complaint
                    </h1>
                    <span className="px-6 py-3 rounded-full shadow-xl text-lg font-semibold bg-[#3d0707] text-white transform hover:scale-105 transition-transform duration-300">
                        Upholding Professional Standards
                    </span>
                </div>
            </div>

            <div className="container mx-auto mt-8 px-8">
                <h3 className="font-black leading-none uppercase dark:text-white text-center mb-6">
                    Submit a Complaint
                </h3>

                <div className="px-4 lg:px-8">
                    <p className="font-bold mb-1">Complaints Handling Procedure</p>
                    <p className="mb-4">
                        Welcome to the Kenya Land Surveyors Board (LSB). We are committed to
                        upholding the highest standards of professionalism and integrity in
                        the field of land surveying. As part of our dedication to serving
                        the public interest, we have established a robust complaints
                        handling procedure in accordance with the Survey Act Cap 299.
                    </p>

                    <p className="font-bold mb-1">Submitting a Complaint</p>
                    <p className="mb-4">
                        If you have any concerns or complaints related to the conduct or
                        services provided by a registered land surveyor, we encourage you to
                        bring them to our attention. You can submit your complaint through
                        the following channels:
                    </p>

                    <div className="mb-4 flex flex-col justify-start items-start">
                        <List
                            requirement="Online Complaint Form"
                            description="Fill out our online complaint form below, providing detailed information about the nature of your complaint and any supporting documentation you may have."
                        />

                        <List
                            requirement="Postal Mail"
                            description="You can also submit your complaint by mail to the following address:"
                        />

                        <div className="block text-center mt-2 pl-16" style={{ fontSize: 'calc(.5em + .3vw)' }}>
                            Land Surveyors Board <br /> P.O. Box 30046-00100 <br /> Nairobi,
                            Kenya
                        </div>
                    </div>

                    <div className="mb-6 flex flex-col justify-start items-start">
                        <p className="font-bold text-gray-700 mb-4">Procedure</p>

                        <List
                            requirement="Acknowledgement"
                            description="We will acknowledge receipt of your complaint promptly and provide you with a reference number for tracking purposes"
                        />
                        <List
                            requirement="Investigation"
                            description="Our team will thoroughly investigate the allegations raised in your complaint, gathering relevant information and evidence."
                        />
                        <List
                            requirement="Resolution"
                            description="Based on the findings of our investigation, we will take appropriate action to address the issues raised in your complaint. This may include disciplinary measures against the registered land surveyor if misconduct is found."
                        />
                        <List
                            requirement="Communication"
                            description="Throughout the process, we will keep you informed of the progress of your complaint and any outcomes or decisions reached."
                        />
                    </div>

                    <p className="font-bold mb-2">Confidentiality & Fairness</p>

                    <p className="mb-6">
                        We treat all complaints with the utmost confidentiality and ensure
                        that the complaints handling process is fair and transparent. We are
                        committed to protecting the rights of both complainants and
                        respondents, and we strive to reach a resolution that is equitable
                        and just.
                    </p>
                </div>

                <form className="space-y-6 px-4 lg:px-8 border-t mt-8 pt-3" onSubmit={handleSubmit} noValidate>
                    {/* Accused Section */}
                    <div>
                        <h4 className="text-xl font-bold text-center text-red-800 uppercase mb-2">Accused Surveyor *</h4>

                        <div ref={surveyorSearchRef} className="relative w-full group">
                            <label htmlFor="surveyor-search" className="font-medium text-gray-700 mb-1 text-center">Search for a Licensed Surveyor by Name or License No.</label>

                            <input
                                type="text"
                                id="surveyor-search"
                                className={`w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all ease-in-out ${selectedSurveyor ? "border-green-500" : "border-gray-300"}`}
                                placeholder={loadingSurveyors ? "Loading surveyors..." : surveyorError ? "Unable to load surveyors" : "Type to search..."}
                                value={searchVal}
                                onChange={handleSurveyorInput}
                                onFocus={() => setSurveyorDropdownVisible(true)}
                                required
                                autoComplete="off"
                                disabled={!!surveyorError || loadingSurveyors}
                            />
                            {surveyorDropdownVisible && !loadingSurveyors && !surveyorError && (
                                <ul className="absolute z-20 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg mt-1">
                                    {filteredSurveyors.length > 0 ? (
                                        filteredSurveyors.map((surveyor) => (
                                            <li
                                                key={surveyor.id}
                                                className="py-2 cursor-pointer hover:bg-[#ebcab3]/30 transition-colors"
                                                onClick={() => handleSurveyorSelect(surveyor)}
                                            >
                                                <span className="font-semibold">{surveyor.name}</span>
                                                <span className="text-gray-500">Lic#: {surveyor.licence_number}</span>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="px-4 py-3 text-sm text-gray-500">No results found.</li>
                                    )}
                                </ul>
                            )}
                            {loadingSurveyors && <div className="mt-2 text-sm text-gray-500">Loading...</div>}
                            {(!selectedSurveyor && searchVal) && <span className="text-xs text-red-500 block mt-1">Please select a surveyor from the dropdown.</span>}
                            {selectedSurveyor && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between animate-fadeIn">
                                    <div>
                                        <p className="font-semibold text-green-800">{selectedSurveyor.name}</p>
                                        <p className="text-sm text-green-600">License No: {selectedSurveyor.licence_number}</p>
                                    </div>
                                    <span className="text-2xl text-green-500">✓</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <strong className="font-bold text-yellow-800">NB:</strong> For a complaint regarding an assistant surveyor, provide the name of the supervising Licensed Surveyor and the assistant’s name in the message. Click <a href="/register" target="_blank" className="text-blue-600 hover:underline">here</a> to access supervisor records.
                        </div>
                    </div>

                    {/* Complaint Details */}
                    <div className="space-y-6">
                        <input
                            type="text"
                            className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                            placeholder="Subject of Complaint"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                        />
                        <textarea
                            className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                            placeholder="Details & Location of Complaint *"
                            rows="6"
                            required
                            value={complaintDetails}
                            onChange={e => setComplaintDetails(e.target.value)}
                        />
                    </div>

                    {/* Complainant Details */}
                    <div>
                        <h4 className="text-xl font-bold text-center text-red-800 uppercase mb-4">Complainant Details *</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <input type="text" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="First Name" required value={firstName} onChange={e => setFirstName(e.target.value)} />
                            <input type="text" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="Last Name" required value={lastName} onChange={e => setLastName(e.target.value)} />
                            <input type="tel" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="Phone Number" required value={phone} onChange={e => setPhone(e.target.value)} />
                            <input type="email" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="Email Address" required value={email} onChange={e => setEmail(e.target.value)} />
                            <input type="text" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="County" required value={county} onChange={e => setCounty(e.target.value)} />
                            <input type="text" className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent" placeholder="Sub-County" required value={subcounty} onChange={e => setSubCounty(e.target.value)} />
                        </div>
                    </div>

                    {/* Evidence Upload */}
                    <div>
                        <h4 className="text-xl font-bold text-center text-red-800 uppercase mb-4">Supporting Evidence *</h4>
                        <div className="flex items-center justify-center w-full">
                            <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                <div className="flex flex-col items-center justify-center text-center p-6">
                                    <CloudUpload className="w-8 h-8 mb-2" />

                                    <p className="mb-2 text-sm text-gray-600">Click to upload or drag and drop</p>

                                    <p className="text-xs text-gray-500">JPG, JPEG, PNG, PDF (MAX. 10MB, 1 file)</p>
                                </div>
                                <input id="dropzone-file" type="file" className="hidden" accept={ALLOWED_EXTENSIONS.join(",")} required={files.length === 0} onChange={handleFilesChange} />
                            </label>
                        </div>
                        <div className="mt-4">
                            {files.length > 0 && (
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                                    <p className="text-sm text-blue-800 truncate">{files[0].name} ({(files[0].size / 1024 / 1024).toFixed(2)} MB)</p>
                                    <button type="button" className="text-red-600 hover:text-red-800 font-bold ml-4" onClick={handleRemoveFile}>Remove</button>
                                </div>
                            )}
                            {evidenceError && <span className="text-xs text-red-500 block mt-1">{evidenceError}</span>}
                        </div>
                    </div>

                    {/* Confirmation */}
                    <div className="py-4 flex items-start">
                        <input
                            type="checkbox"
                            className="h-5 w-5 mt-1 text-[#3d0707] focus:ring-[#5a1a1a] border-gray-300 rounded"
                            id="flexCheckDefault"
                            required
                            checked={confirmationChecked}
                            onChange={e => setConfirmationChecked(e.target.checked)}
                        />
                        <label htmlFor="flexCheckDefault" className="ml-3 block text-sm text-gray-700">
                            <strong>I confirm that:</strong> I have read and understood the complaints procedure, and the information I have submitted is factual and true to the best of my knowledge.
                        </label>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={submitting || !isFormValid()}
                            className="w-full md:w-auto px-8 py-2 rounded-lg font-bold text-lg text-white bg-[#3d0707] hover:bg-[#5a1a1a] focus:outline-none focus:ring-4 focus:ring-[#3d0707]/50 transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center shadow-lg transform hover:scale-105"
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Submit Complaint
                                </>
                            )}
                        </button>
                    </div>
                </form>

                <p className="py-6 px-4 text-center mx-auto max-w-4xl border-t-2 border-[#ebcab3] border-dashed mt-12">
                    Thank you for your cooperation in helping us maintain the integrity and professionalism of the land surveying profession in Kenya.
                </p>
            </div>
        </div>
    );
}

export default Complaints;
